import { useEffect, useState } from 'react';
import profileService from '../api/services/profileService';
import { getTimezoneOffsetMinutes } from '../utils/dateHelpers';
import type { UserConnectionItem } from '../types/userProfile';

const PAGE_SIZE = 100;
const MAX_PAGES = 10;

export const includesUser = (
  users: readonly UserConnectionItem[],
  targetUserId: number,
): boolean => users.some(user => Number(user.userId) === targetUserId);

interface IncomingBuddyRequestResult {
  incoming: boolean | null;
  loading: boolean;
}

/**
 * Checks the viewer's authoritative incoming BuddyInvite list. Other-profile
 * payloads can report only a directionless `pending` status, which is not
 * enough to decide between Accept and Cancel.
 */
export default function useIncomingBuddyRequest(
  viewerUserId?: number | string | null,
  targetUserId?: number | string | null,
  enabled = false,
): IncomingBuddyRequestResult {
  const viewerId = Number(viewerUserId);
  const targetId = Number(targetUserId);
  const canCheck =
    enabled &&
    Number.isFinite(viewerId) &&
    viewerId > 0 &&
    Number.isFinite(targetId) &&
    targetId > 0;
  const [incoming, setIncoming] = useState<boolean | null>(null);
  const [loading, setLoading] = useState(canCheck);

  useEffect(() => {
    if (!canCheck) {
      setIncoming(null);
      setLoading(false);
      return;
    }

    const controller =
      typeof AbortController !== 'undefined' ? new AbortController() : undefined;
    let cancelled = false;

    setIncoming(null);
    setLoading(true);

    const check = async (): Promise<void> => {
      for (let page = 1; page <= MAX_PAGES; page += 1) {
        const response = await profileService.getUserConnections(
          {
            profileUserId: viewerId,
            relation: 'BuddyInvite',
            startIndex: page,
            pageSize: PAGE_SIZE,
            timezoneOffset: getTimezoneOffsetMinutes(),
          },
          controller?.signal,
        );
        const users = response?.data?.users ?? [];
        if (includesUser(users, targetId)) {
          if (!cancelled) setIncoming(true);
          return;
        }
        if (users.length < PAGE_SIZE) {
          if (!cancelled) setIncoming(false);
          return;
        }
      }
      if (!cancelled) setIncoming(false);
    };

    check()
      .catch(() => {
        if (!cancelled && !controller?.signal.aborted) setIncoming(null);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
      controller?.abort();
    };
  }, [canCheck, targetId, viewerId]);

  return { incoming, loading };
}
